import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, MessageSquareReply } from "lucide-react";
import { useInquirySettings } from "@/lib/inquiry";
import { downloadFile } from "@/components/InvoiceRegister";
import { InquiryFilterBar, EMPTY_FILTERS, filterParams } from "@/components/inquiries/InquiryFilterBar";
import { OverviewTiles } from "@/components/inquiries/OverviewTiles";
import { AgingChart, ClosureChart, Panel } from "@/components/inquiries/OverviewCharts";
import { BreakdownTable } from "@/components/inquiries/BreakdownTable";

const OldestOpen = ({ rows, onOpen }) => (
  <Panel title="Oldest open inquiries" sub="Longest waiting first" testid="ov-oldest">
    {rows.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No open inquiries.</div> : rows.map((r) => (
      <button key={r.id} type="button" data-testid={`ov-oldest-${r.id}`} onClick={() => onOpen(r.id)} className="w-full text-left flex items-center gap-3 py-2 border-b border-slate-50 last:border-0 hover:bg-blue-50/40">
        <span className="mono text-xs text-blue-700 w-28 shrink-0">{r.number}</span>
        <span className="flex-1 truncate text-sm">{r.subject}{r.awaiting_reply && <MessageSquareReply className="inline w-3.5 h-3.5 ml-1.5 text-amber-600" aria-label="Awaiting our reply" />}</span>
        <span className="text-xs text-slate-500 w-28 truncate">{r.assigned_name || "—"}</span>
        <span className="mono text-xs w-12 text-right text-slate-900">{r.age}d</span>
      </button>
    ))}
  </Panel>
);

export default function InquiryOverview() {
  const nav = useNavigate();
  const [settings] = useInquirySettings();
  const [f, setF] = useState(EMPTY_FILTERS);
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD((await api.get("/inquiry-reports/dashboard", { params: filterParams(f) })).data); } catch (e) { toast.error(formatErr(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const go = (extra) => {
    const base = filterParams({ ...f, date_from: "", date_to: "" });
    nav(`/inquiries?${new URLSearchParams({ state: "all", ...base, ...extra })}`);
  };
  return (
    <div data-testid="inquiry-overview-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Inquiries</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Inquiry Overview</h1>
        </div>
        <Button data-testid="ov-export-contacts" variant="outline" onClick={() => downloadFile("/inquiry-reports/contacts.xlsx", filterParams(f), "inquiry-contact-report.xlsx")} className="gap-2 bg-white">
          <FileSpreadsheet className="w-4 h-4 text-emerald-700" />Date-wise contact report (Excel)
        </Button>
      </div>
      <InquiryFilterBar f={f} setF={setF} settings={settings} />
      <p className="text-[11px] text-slate-500 -mt-3">Dates filter by inquiry date on this page. In the Excel report they filter by interaction date, and “user” means who handled the interaction.</p>
      {!d ? <div className="text-sm text-slate-500" data-testid="ov-loading">Loading…</div> : (
        <>
          <OverviewTiles t={d.totals} go={go} />
          <div className="grid lg:grid-cols-3 gap-4">
            <AgingChart data={d.aging} />
            <ClosureChart data={d.closure_reasons} />
            <OldestOpen rows={d.oldest_open} onOpen={(id) => nav(`/inquiries/${id}`)} />
          </div>
          <div className="grid xl:grid-cols-2 gap-4">
            <BreakdownTable title="By assigned user" testid="ov-by-user" rows={d.by_user} onRow={(r) => r.key && go({ assigned_to: r.key })} />
            <BreakdownTable title="By channel" testid="ov-by-channel" rows={d.by_channel} onRow={(r) => go({ channel: r.key })} />
          </div>
          <BreakdownTable title="By category" testid="ov-by-category" rows={d.by_category} onRow={(r) => go({ category: r.key })} />
        </>
      )}
    </div>
  );
}
